import { useCallback, useMemo, useTransition } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import type { ViewState } from "~/components/timeline";
import type {
  EntityListFilters,
  EntitySortOrder,
  EntityView,
  MapViewport,
  SortDirection,
} from "~/features/entities/types";
import { v2Route } from "~/v2/routes";
import { EntitiesPresenter } from "./entities.presenter";

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT: EntitySortOrder = "updatedAt";
const DEFAULT_DIRECTION: SortDirection = "desc";
/** Default canvas width used to convert center-year ↔ offsetX before the timeline measures itself. */
const TIMELINE_CANVAS_WIDTH = 800;

const splitIds = (value: string | undefined): string[] => (value ? value.split(",").filter((id) => id.length > 0) : []);

export function EntitiesContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.entities);
  const [isPending, startTransition] = useTransition();

  const view: EntityView = params.view ?? "table";
  const sortOrder: EntitySortOrder = params.sortOrder ?? DEFAULT_SORT;
  const direction: SortDirection = params.sortOrderDirection ?? DEFAULT_DIRECTION;
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;

  const filters: EntityListFilters = useMemo(
    () => ({
      idOrName: params.idOrName,
      categoryIds: splitIds(params.categoryId),
      referenceIds: splitIds(params.referenceId),
      excludeDeactivated: params.excludeDeactivated ?? true,
      gdtfFrom: params.gdtfFrom,
      gdtfTo: params.gdtfTo,
    }),
    [params.idOrName, params.categoryId, params.referenceId, params.excludeDeactivated, params.gdtfFrom, params.gdtfTo],
  );

  const onApplyFilters = useCallback(
    (next: EntityListFilters) => {
      setParams((prev) => ({
        ...prev,
        idOrName: next.idOrName || undefined,
        categoryId: next.categoryIds.length ? next.categoryIds.join(",") : undefined,
        referenceId: next.referenceIds.length ? next.referenceIds.join(",") : undefined,
        excludeDeactivated: next.excludeDeactivated ? undefined : false,
        gdtfFrom: next.gdtfFrom || undefined,
        gdtfTo: next.gdtfTo || undefined,
        page: undefined,
      }));
    },
    [setParams],
  );

  // Click-to-filter: add the clicked category chip to the categoryId filter (deduped),
  // mirroring V1's EntitiesListTable onCategoryClick. The synced search form reflects it.
  const onAddCategory = useCallback(
    (categoryId: string) => {
      setParams((prev) => {
        const current = splitIds(prev.categoryId);
        if (current.includes(categoryId)) return prev;
        return { ...prev, categoryId: [...current, categoryId].join(","), page: undefined };
      });
    },
    [setParams],
  );

  const onViewChange = useCallback(
    (next: EntityView) => {
      // Keep each view's state (timeline pan/zoom, map year-range + viewport) in the URL so
      // switching tabs and back restores it.
      setParams((prev) => ({ ...prev, view: next === "table" ? undefined : next }));
    },
    [setParams],
  );

  const onSortingChange = useCallback(
    (nextOrder: EntitySortOrder, nextDirection: SortDirection) => {
      startTransition(() => {
        setParams((prev) => ({
          ...prev,
          sortOrder: nextOrder === DEFAULT_SORT ? undefined : nextOrder,
          sortOrderDirection: nextDirection === DEFAULT_DIRECTION ? undefined : nextDirection,
          page: undefined,
        }));
      });
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
      });
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({
          ...prev,
          pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
          page: undefined,
        }));
      });
    },
    [setParams],
  );

  const selectedEntity = params.selectedEntity ?? null;
  const selectedGeoItem = params.geoItemId ?? null;
  const onSelect = useCallback(
    (id: string) => setParams((prev) => ({ ...prev, selectedEntity: id, geoItemId: undefined })),
    [setParams],
  );
  const onSelectArea = useCallback(
    (id: string, geoItemId: string) => setParams((prev) => ({ ...prev, selectedEntity: id, geoItemId })),
    [setParams],
  );
  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedEntity: undefined, geoItemId: undefined })),
    [setParams],
  );

  // ── Timeline / map view state ↔ URL (read once on mount; pan/zoom replaces history) ──
  // Read mount-time params only; the canvas owns subsequent state. Deps are intentionally
  // empty so a URL write from pan/zoom doesn't reset the view.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialTimelineViewState = useMemo<ViewState | undefined>(() => {
    const { tlCenterYear, tlScale } = params;
    if (tlCenterYear == null || tlScale == null) return undefined;
    return { offsetX: TIMELINE_CANVAS_WIDTH / 2 - tlCenterYear * tlScale, offsetY: 0, scale: tlScale };
  }, []);
  const onTimelineViewStateChange = useCallback(
    (centerYear: number, scale: number) => {
      setParams((prev) => ({ ...prev, tlCenterYear: centerYear, tlScale: scale }), { replace: true });
    },
    [setParams],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialMapYearRange = useMemo<[number, number] | undefined>(() => {
    const { mapYearMin, mapYearMax } = params;
    return mapYearMin == null || mapYearMax == null ? undefined : [mapYearMin, mapYearMax];
  }, []);
  const onMapYearRangeChange = useCallback(
    (range: [number, number]) => {
      setParams((prev) => ({ ...prev, mapYearMin: range[0], mapYearMax: range[1] }), { replace: true });
    },
    [setParams],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialMapViewport = useMemo<MapViewport | undefined>(() => {
    const { mapLat, mapLng, mapZoom } = params;
    if (mapLat == null || mapLng == null || mapZoom == null) return undefined;
    return { latitude: mapLat, longitude: mapLng, zoom: mapZoom };
  }, []);
  const onMapViewportChange = useCallback(
    (viewport: MapViewport) => {
      setParams(
        (prev) => ({ ...prev, mapLat: viewport.latitude, mapLng: viewport.longitude, mapZoom: viewport.zoom }),
        { replace: true },
      );
    },
    [setParams],
  );

  return (
    <EntitiesPresenter
      view={view}
      onViewChange={onViewChange}
      filters={filters}
      onApplyFilters={onApplyFilters}
      sortOrder={sortOrder}
      direction={direction}
      onSortingChange={onSortingChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      isPending={isPending}
      onSelect={onSelect}
      onSelectArea={onSelectArea}
      onAddCategory={onAddCategory}
      selectedEntity={selectedEntity}
      selectedGeoItem={selectedGeoItem}
      onCloseSelected={onCloseSelected}
      initialTimelineViewState={initialTimelineViewState}
      onTimelineViewStateChange={onTimelineViewStateChange}
      initialMapYearRange={initialMapYearRange}
      onMapYearRangeChange={onMapYearRangeChange}
      initialMapViewport={initialMapViewport}
      onMapViewportChange={onMapViewportChange}
    />
  );
}
